import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  TituloDeSecao,
} from '../componentes/base';
import { Campo, Caixa } from '../componentes/campos';
import { useSessao } from '../sessao/SessaoProvedor';

interface Parametros {
  readonly nomeEscritorio: string;
  readonly nomeAdvogada: string;
  readonly telefoneEscritorio: string | null;
  readonly chavePix: string | null;
  readonly assinaturaMensagem: string;
  readonly cobrancaDiariaEmAtraso: boolean;
  readonly limiteDiasAtraso: number;
  readonly diasEntreRetomadas: number;
  readonly diasParaAtrasoGrave: number;
  readonly horaRotina: number;
  readonly minutoRotina: number;
  readonly diaFechamento: number;
  readonly diasDeLembrete: number[];
  readonly documentosExigidos: Array<{ tipo: string; rotulo: string }>;
}

export function PaginaConfiguracoes() {
  const clienteConsulta = useQueryClient();
  const { usuaria } = useSessao();
  const [rascunho, setRascunho] = useState<Partial<Parametros>>({});
  const [aviso, setAviso] = useState<{ texto: string; tom: 'recebido' | 'vencido' } | null>(null);

  const { data: parametros, isLoading } = useQuery({
    queryKey: ['parametros'],
    queryFn: () => api.buscar<Parametros>('/parametros'),
  });

  const { data: provedor } = useQuery({
    queryKey: ['provedor'],
    queryFn: () =>
      api.buscar<{ nome: string; enviaAutomaticamente: boolean; ok: boolean; detalhe: string }>(
        '/cobranca/provedor',
      ),
  });

  useEffect(() => {
    if (parametros) setRascunho(parametros);
  }, [parametros]);

  const salvar = useMutation({
    mutationFn: () => api.substituir<Parametros>('/parametros', rascunho),
    onSuccess: () => {
      setAviso({ texto: 'Configurações salvas.', tom: 'recebido' });
      void clienteConsulta.invalidateQueries({ queryKey: ['parametros'] });
    },
    onError: (e) =>
      setAviso({
        texto: e instanceof FalhaDaApi ? e.message : 'Não foi possível salvar.',
        tom: 'vencido',
      }),
  });

  const trocarSenha = useMutation({
    mutationFn: ({ atual, nova }: { atual: string; nova: string }) =>
      api.criar<{ sessoesEncerradas: number }>('/usuarios/senha', {
        senhaAtual: atual,
        senhaNova: nova,
      }),
    onSuccess: (r) => {
      setSenhaAtual('');
      setSenhaNova('');
      setAviso({
        texto:
          'Senha alterada.' +
          (r.sessoesEncerradas > 0
            ? ` ${r.sessoesEncerradas} outra(s) sessão(ões) foram encerradas.`
            : ''),
        tom: 'recebido',
      });
    },
    onError: (e) =>
      setAviso({
        texto: e instanceof FalhaDaApi ? e.message : 'Não foi possível trocar a senha.',
        tom: 'vencido',
      }),
  });

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');

  if (isLoading || !parametros) return <Carregando />;

  const definir = <C extends keyof Parametros>(campo: C, valor: Parametros[C]) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const numero = (campo: keyof Parametros) => String(rascunho[campo] ?? '');

  return (
    <>
      <CabecalhoDaPagina
        titulo="Configurações"
        descricao="Dados do escritório, régua de cobrança e rotina automática"
        acoes={
          <Botao tipo="primario" onClick={() => salvar.mutate()} desabilitado={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Botao>
        }
      />

      {aviso && (
        <div className="mb-5">
          <Alerta tom={aviso.tom}>{aviso.texto}</Alerta>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Cartao>
          <TituloDeSecao>Escritório</TituloDeSecao>
          <div className="flex flex-col gap-4">
            <Campo
              rotulo="Nome do escritório"
              valor={String(rascunho.nomeEscritorio ?? '')}
              aoMudar={(v) => definir('nomeEscritorio', v)}
              auxilio="aparece nas mensagens e nos documentos"
            />
            <Campo
              rotulo="Advogada responsável"
              valor={String(rascunho.nomeAdvogada ?? '')}
              aoMudar={(v) => definir('nomeAdvogada', v)}
            />
            <Campo
              rotulo="Telefone do escritório"
              valor={String(rascunho.telefoneEscritorio ?? '')}
              aoMudar={(v) => definir('telefoneEscritorio', v)}
            />
            <Campo
              rotulo="Chave PIX do escritório"
              valor={String(rascunho.chavePix ?? '')}
              aoMudar={(v) => definir('chavePix', v)}
              auxilio="usada só quando a clínica não tiver chave própria — o devedor paga à clínica"
            />
            <Campo
              rotulo="Assinatura das mensagens"
              valor={String(rascunho.assinaturaMensagem ?? '')}
              aoMudar={(v) => definir('assinaturaMensagem', v)}
            />
          </div>
        </Cartao>

        <Cartao>
          <TituloDeSecao>Régua de cobrança</TituloDeSecao>
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-1 text-xs font-medium text-[color:var(--texto-suave)]">
                Lembretes antes do vencimento
              </p>
              <div className="flex gap-1.5">
                {parametros.diasDeLembrete.map((d) => (
                  <Ficha key={d} tom="acento">
                    {d} {d === 1 ? 'dia' : 'dias'}
                  </Ficha>
                ))}
              </div>
            </div>

            <Caixa
              rotulo="Cobrar todo dia enquanto a parcela estiver em atraso"
              marcado={Boolean(rascunho.cobrancaDiariaEmAtraso)}
              aoMudar={(v) => definir('cobrancaDiariaEmAtraso', v)}
            />

            <div className="grid grid-cols-2 gap-4">
              <Campo
                rotulo="Aviso final a partir de (dias)"
                valor={numero('diasParaAtrasoGrave')}
                aoMudar={(v) => definir('diasParaAtrasoGrave', Number(v))}
              />
              <Campo
                rotulo="Parar de cobrar após (dias)"
                valor={numero('limiteDiasAtraso')}
                aoMudar={(v) => definir('limiteDiasAtraso', Number(v))}
                auxilio="depois disso, o caso vai para a triagem judicial"
              />
              <Campo
                rotulo="Intervalo entre retomadas (dias)"
                valor={numero('diasEntreRetomadas')}
                aoMudar={(v) => definir('diasEntreRetomadas', Number(v))}
                auxilio="para devedor sem acordo"
              />
              <Campo
                rotulo="Dia do repasse das clínicas"
                valor={numero('diaFechamento')}
                aoMudar={(v) => definir('diaFechamento', Number(v))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Campo
                rotulo="Hora da rotina diária"
                valor={numero('horaRotina')}
                aoMudar={(v) => definir('horaRotina', Number(v))}
                auxilio="0 a 23, horário de Brasília"
              />
              <Campo
                rotulo="Minuto"
                valor={numero('minutoRotina')}
                aoMudar={(v) => definir('minutoRotina', Number(v))}
              />
            </div>

            <p className="text-xs text-[color:var(--texto-tenue)]">
              Cada devedor recebe no máximo <strong>uma mensagem por dia</strong> — a de maior
              gravidade entre as que ele merece.
            </p>
          </div>
        </Cartao>

        <Cartao>
          <TituloDeSecao>Envio de mensagens</TituloDeSecao>
          {provedor && (
            <>
              <div className="mb-3 flex items-center gap-2">
                <Ficha tom={provedor.ok ? 'recebido' : 'vencido'}>{provedor.nome}</Ficha>
                {provedor.enviaAutomaticamente ? (
                  <Ficha tom="acento">Envio automático</Ficha>
                ) : (
                  <Ficha tom="neutro">Despacho manual</Ficha>
                )}
              </div>
              <p className="text-sm text-[color:var(--texto-suave)]">{provedor.detalhe}</p>
            </>
          )}
        </Cartao>

        <Cartao>
          <TituloDeSecao>Documentos exigidos para ajuizar</TituloDeSecao>
          <ul className="flex flex-col gap-1.5 text-sm">
            {parametros.documentosExigidos.map((d) => (
              <li key={d.tipo} className="text-[color:var(--texto-suave)]">
                • {d.rotulo}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-[color:var(--texto-tenue)]">
            Cada item exige o anexo, a assinatura quando cabível, e a conferência expressa de
            uma colaboradora. Sem isso, o servidor recusa a confirmação de aptidão.
          </p>
        </Cartao>

        <Cartao>
          <TituloDeSecao auxiliar={usuaria?.login}>Minha senha</TituloDeSecao>
          <div className="flex flex-col gap-4">
            <Campo
              rotulo="Senha atual"
              tipo="password"
              valor={senhaAtual}
              aoMudar={setSenhaAtual}
            />
            <Campo
              rotulo="Nova senha"
              tipo="password"
              valor={senhaNova}
              aoMudar={setSenhaNova}
              auxilio="ao menos 8 caracteres, com letra e número"
            />
            <div>
              <Botao
                onClick={() => trocarSenha.mutate({ atual: senhaAtual, nova: senhaNova })}
                desabilitado={trocarSenha.isPending || !senhaAtual || !senhaNova}
              >
                Trocar senha
              </Botao>
            </div>
            <p className="text-xs text-[color:var(--texto-tenue)]">
              Trocar a senha encerra as suas outras sessões abertas.
            </p>
          </div>
        </Cartao>
      </div>
    </>
  );
}
